import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useQuery } from '@tanstack/react-query';
import { formatPrice } from '@/lib/utils';

const S = {
  font: "'Sora', system-ui, sans-serif",
  heading: "'Fraunces', Georgia, serif",
};

export default function SoleilExamsPage() {
  const { t, i18n } = useTranslation();
  const isRTL = i18n.language === 'ar';

  const { data: exams, isLoading } = useQuery({
    queryKey: ['public-exams'],
    queryFn: async () => {
      const res = await fetch('/api/exams');
      if (!res.ok) throw new Error('Failed to fetch exams');
      return res.json();
    },
  });

  return (
    <div style={{ background: 'var(--soleil-cream)', minHeight: '100vh' }}>
      {/* Hero */}
      <section style={{ padding: '72px 24px 56px', textAlign: 'center' }}>
        <div style={{ maxWidth: 720, margin: '0 auto' }}>
          <div style={{ fontFamily: S.font, fontSize: 11, fontWeight: 700, color: 'var(--soleil-sienna)', letterSpacing: '0.2em', marginBottom: 14 }}>
            {isRTL ? 'الامتحانات' : 'CERTIFICATION EXAMS'}
          </div>
          <h1 style={{ fontFamily: S.heading, fontSize: 'clamp(32px, 5vw, 48px)', fontWeight: 600, color: 'var(--soleil-ink)', marginBottom: 16 }}>
            {isRTL ? 'احصل على شهادتك ' : 'Get your '}
            <span style={{ color: 'var(--soleil-gold)', fontStyle: 'italic' }}>
              {isRTL ? 'المعتمدة' : 'certified'}
            </span>
          </h1>
          <p style={{ fontFamily: S.font, fontSize: 15, color: 'var(--soleil-sub)', lineHeight: 1.7, maxWidth: 540, margin: '0 auto' }}>
            {isRTL
              ? 'قدّم امتحانات الشهادات الرسمية من PeopleCert. اختر بين الامتحان الإلكتروني أو الحضوري في بغداد.'
              : 'Take official PeopleCert certification exams. Choose between online computer-based or in-person paper-based exams in Baghdad.'}
          </p>
        </div>
      </section>

      {/* Exam Cards */}
      <section style={{ padding: '0 24px 96px' }}>
        <div style={{ maxWidth: 1060, margin: '0 auto' }}>
          {isLoading ? (
            <div style={{ textAlign: 'center', padding: 48, fontFamily: S.font, color: 'var(--soleil-mut)' }}>
              {isRTL ? 'جاري التحميل...' : 'Loading...'}
            </div>
          ) : !exams?.length ? (
            <div style={{ textAlign: 'center', padding: 48, fontFamily: S.font, color: 'var(--soleil-mut)' }}>
              {isRTL ? 'لا توجد امتحانات متاحة حالياً' : 'No exams available at the moment'}
            </div>
          ) : (
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(340px, 1fr))', gap: 24 }}>
              {exams.map((exam: any) => (
                <div key={exam.id} style={{ background: '#fff', borderRadius: 20, border: '1px solid var(--soleil-sand)', overflow: 'hidden', transition: 'transform 0.2s, box-shadow 0.2s' }}
                    onMouseEnter={e => { (e.currentTarget as HTMLElement).style.transform = 'translateY(-4px)'; (e.currentTarget as HTMLElement).style.boxShadow = '0 12px 32px rgba(0,0,0,0.08)'; }}
                    onMouseLeave={e => { (e.currentTarget as HTMLElement).style.transform = 'translateY(0)'; (e.currentTarget as HTMLElement).style.boxShadow = 'none'; }}
                  >
                    {/* Card top accent */}
                    <div style={{ height: 4, background: 'linear-gradient(90deg, var(--soleil-gold), var(--soleil-sienna))' }} />
                    
                    <div style={{ padding: '28px 28px 24px' }}>
                      {/* Badge */}
                      <div style={{ display: 'inline-block', fontFamily: S.font, fontSize: 10, fontWeight: 700, letterSpacing: '0.12em', color: 'var(--soleil-gold-dark)', background: 'var(--soleil-gold-pale)', padding: '5px 12px', borderRadius: 20, marginBottom: 16 }}>
                        {isRTL ? 'شهادة PeopleCert' : 'PEOPLECERT CERTIFIED'}
                      </div>

                      <h3 style={{ fontFamily: S.heading, fontSize: 22, fontWeight: 600, color: 'var(--soleil-ink)', marginBottom: 10 }}>
                        {isRTL ? exam.titleAr : exam.titleEn}
                      </h3>
                      <p style={{ fontFamily: S.font, fontSize: 13.5, color: 'var(--soleil-sub)', lineHeight: 1.7, marginBottom: 20 }}>
                        {isRTL ? exam.shortDescriptionAr : exam.shortDescriptionEn}
                      </p>

                      {/* Exam type options */}
                      <div style={{ display: 'flex', gap: 12, marginBottom: 20 }}>
                        <div style={{ flex: 1, padding: '12px 14px', borderRadius: 12, border: '1px solid var(--soleil-sand)', background: 'var(--soleil-cream)' }}>
                          <div style={{ fontFamily: S.font, fontSize: 10, fontWeight: 700, color: 'var(--soleil-mut)', letterSpacing: '0.1em', marginBottom: 4 }}>
                            💻 {isRTL ? 'إلكتروني' : 'COMPUTER'}
                          </div>
                          <div style={{ fontFamily: S.heading, fontSize: 18, fontWeight: 700, color: 'var(--soleil-ink)' }}>
                            {exam.computerPrice?.toLocaleString()} <span style={{ fontSize: 12, fontWeight: 400, color: 'var(--soleil-mut)' }}>{exam.currency}</span>
                          </div>
                        </div>
                        {exam.paperPrice != null && (
                        <div style={{ flex: 1, padding: '12px 14px', borderRadius: 12, border: '1px solid var(--soleil-sand)', background: 'var(--soleil-cream)' }}>
                          <div style={{ fontFamily: S.font, fontSize: 10, fontWeight: 700, color: 'var(--soleil-mut)', letterSpacing: '0.1em', marginBottom: 4 }}>
                            📝 {isRTL ? 'ورقي — بغداد' : 'PAPER — BAGHDAD'}
                          </div>
                          <div style={{ fontFamily: S.heading, fontSize: 18, fontWeight: 700, color: 'var(--soleil-ink)' }}>
                            {exam.paperPrice?.toLocaleString()} <span style={{ fontSize: 12, fontWeight: 400, color: 'var(--soleil-mut)' }}>{exam.currency}</span>
                          </div>
                        </div>
                        )}
                      </div>

                      {/* CTA */}
                      <Link to={'/exams/' + exam.slug} style={{ display: 'block', fontFamily: S.font, fontSize: 14, fontWeight: 700, color: '#fff', background: 'var(--soleil-gold)', padding: '12px 0', borderRadius: 50, textAlign: 'center', letterSpacing: '0.02em', textDecoration: 'none', transition: 'opacity 0.2s' }}
                        onMouseEnter={e => { (e.currentTarget as HTMLElement).style.opacity = '0.9'; }}
                        onMouseLeave={e => { (e.currentTarget as HTMLElement).style.opacity = '1'; }}>
                        {isRTL ? 'عرض التفاصيل والتسجيل' : 'View Details & Register'}
                      </Link>
                    </div>
                  </div>
              ))}
            </div>
          )}
        </div>
      </section>
    </div>
  );
}
